/**
 * Single source of truth for the API base URL, auto-detecting local
 * dev-stacks tunnel access.
 *
 * `NEXT_PUBLIC_API_URL` is a build/container-time constant — fine for a real
 * deployment (Vercel, dashboard.airbills.co, staging.airbills.co) where the
 * frontend only ever talks to one backend, but wrong for the local
 * containerized dev-stacks setup (`local-infra/dev-stacks`): frontend and
 * backend are two SEPARATE Cloudflare-tunneled hostnames
 * (`probiller-frontend.gg-dev.space` / `probiller.gg-dev.space`), and the
 * container's env is frozen at creation time — podman's `env_file` only
 * reads `.env` once, on create; a later edit + `podman restart` does NOT
 * pick it up (needs a full recreate). Hand-editing `.env` to point at
 * whichever backend you want that day breaks the OTHER access path every
 * time (see git history 2026-08-27, "resident-portal-mobile-redesign"
 * branch discussion).
 *
 * Fix: read the ACTUAL browser hostname at runtime and only override for the
 * one local-dev domain where a single static env value can't be right for
 * everyone. Every other hostname (plain `localhost`, Vercel previews, and
 * every real deployment) keeps using `NEXT_PUBLIC_API_URL` exactly as
 * before — this is additive, not a behavior change for anyone not on the
 * gg-dev.space tunnel.
 *
 * On gg-dev.space specifically, this resolves to a RELATIVE (same-origin)
 * base rather than the backend's own tunnel hostname
 * (`https://probiller.gg-dev.space`). Reason, found empirically 2026-08-27:
 * a cross-subdomain XHR from `probiller-frontend.gg-dev.space` to
 * `probiller.gg-dev.space` gets intercepted by Cloudflare Access — the
 * browser's Access session for the frontend hostname (established via the
 * page's own full navigation, which Access allows to complete an SSO
 * redirect) does not carry over to a background `fetch`/XHR to the API
 * hostname, which has no way to complete that redirect dance and just
 * fails with no response body. Routing same-origin instead means the
 * browser only ever talks to the ONE Access-authenticated hostname; the
 * matching rewrite in next.config.mjs (`has: [{ type: "host", ... }]`)
 * forwards `/api/v1/*` server-side, straight to the backend over the LAN —
 * bypassing Cloudflare/Access entirely for that internal hop.
 *
 * Every module that previously did `process.env.NEXT_PUBLIC_API_URL`
 * directly should import `API_BASE_URL` from here instead, so there is one
 * place to extend this logic (e.g. a second local hostname pattern) rather
 * than N copies to keep in sync.
 */
export function resolveApiBaseUrl(): string {
  const envUrl = process.env.NEXT_PUBLIC_API_URL || ""
  if (typeof window !== "undefined") {
    const host = window.location.hostname
    if (host.endsWith(".gg-dev.space")) {
      return ""
    }
  }
  return envUrl
}

export const API_BASE_URL = resolveApiBaseUrl()
